/**
 * Wire types for the CMX Document Content read contract, spec 0.1.
 *
 * Mirrors `schemas/cmx-document-content.schema.json` in the cmx-document-content-spec
 * repo (vendored fixtures in ./fixtures record the exact commit). These are the
 * shapes the service returns; the viewer's internal model is in
 * ./cmx-document-content-adapter, and the two are deliberately different -- see
 * that file for why.
 */

/** A code-point range into the body text. `common.schema.json#/$defs/span`. */
export type Span = {
  start: number;
  end: number;
};

export type Attribute = {
  key: string;
  value: string;
};

export type Relation = {
  type: string;
  targetId: string;
};

/**
 * One annotation. Note what is NOT here: no `kind` field and no `parentId`.
 * `kind` travels as an attribute and parentage as a `partOf` relation, and
 * there is exactly one `range`, never a list of spans (ADR-0016).
 */
export type Annotation = {
  id: string;
  range: Span;
  relations?: Relation[];
  attributes: Attribute[];
};

export type AnnotationLayer = {
  specVersion: string;
  bodyDigest: string;
  producer: string;
  derivedFrom?: {
    producer: string;
    bodyDigest: string;
  };
  annotations: Annotation[];
};

export const MEDIA_TYPE_MARKDOWN = 'text/markdown';
export const MEDIA_TYPE_PLAIN = 'text/plain';
export type BodyMediaType = typeof MEDIA_TYPE_MARKDOWN | typeof MEDIA_TYPE_PLAIN;

/**
 * The body at some tier. Only `tier` and `digest` are required by the schema:
 * `text` is absent when a caller asks for `?body=reference`, in which case
 * `uri` points at the bytes instead. Callers must not assume `text`.
 */
export type Body = {
  tier: 1 | 2 | 3;
  digest: string;
  /**
   * `text/plain` for a body from a plain text source, which is never turned
   * into Markdown; `text/markdown` otherwise. The viewer picks its renderer
   * from this. Absent means Markdown.
   */
  mediaType?: BodyMediaType;
  length?: number;
  uri?: string;
  text?: string;
};

/** `GET /documents/cmx-document-content/{cmxDocumentContentId}/layers/{layer}/v1` -> 200. */
export type CmxDocumentContent = {
  specVersion: string;
  documentId: string;
  businessKey?: string;
  documentVersion: number;
  body: Body;
  /** `null` for layers 1-3: an annotation payload needs a tier-3 body. */
  layer: AnnotationLayer | null;
};

/**
 * State of one layer. `document-status.schema.json` pins these four spellings exactly, and
 * `LayerStateEnum` in documentation-service is the server-side mirror.
 *
 * Only `ready` is retrievable. `pending` answers a different question -- *is it finished* -- and
 * is what lets a caller check back instead of spin; `notRun` means nothing is coming unless
 * something requests it. A partially-produced layer is never offered and must never be inferred.
 */
export type LayerState = 'notRun' | 'pending' | 'ready' | 'failed';

/** One layer's entry in the status response. `producer`/`completedAt` appear once it is ready. */
export type LayerStatus = {
  layer: number;
  state: LayerState;
  producer?: string;
  completedAt?: string;
};

/**
 * `GET /documents/cmx-document-content/{cmxDocumentContentId}/v1` -> 200.
 *
 * All five layers are always reported whatever their state, so `notRun` is an answer rather than
 * an absence -- which is what makes this the only endpoint that distinguishes "not yet" from
 * "never". `tier` is the tier of the stored body.
 */
export type CmxDocumentContentStatus = {
  specVersion: string;
  documentId: string;
  acceptedAt: string;
  sourceMediaType: string;
  tier?: 1 | 2 | 3;
  layers: LayerStatus[];
};

/** Closed set at 0.1 for the kinds this viewer acts on. */
export const ANNOTATION_KIND_SECTION = 'section';
export const ANNOTATION_KIND_SECTION_HEADER = 'sectionHeader';

/** Attribute key carrying the annotation kind. */
export const ATTRIBUTE_KEY_KIND = 'kind';

/**
 * Attribute key carrying a section's code, on `kind: section` only. Its value is drawn from the
 * spec's `section-code.schema.json` (CMXDC-ANN-029); absent on an untyped section (CMXDC-ANN-018) and
 * on every L4 section (CMXDC-LAYER-011).
 */
export const ATTRIBUTE_KEY_SECTION_CODE = 'section_code';

/** Relation type expressing parentage. */
export const RELATION_TYPE_PART_OF = 'partOf';
